<!-- MisconductForm.vue -->
<template>
  <v-card rounded="xl" elevation="3" class="overflow-hidden">
    <v-toolbar density="comfortable" color="primary">
      <v-toolbar-title class="text-white">{{ isEdit ? 'Edit Misconduct' : 'Add Misconduct' }}</v-toolbar-title>
      <v-spacer />
      <v-btn icon="mdi-close" variant="text" @click="onClose" />
    </v-toolbar>

    <div class="cl-content">
      <v-form ref="formRef" v-model="valid" @submit.prevent="onSubmit">
        <v-container fluid class="px-0">
          <v-row dense>
            <v-col cols="12" sm="6" md="2">
              <v-select label="Time (min)" v-model.number="miscreport.time" :items="minuteOptions"
                :rules="[req]" hide-details="auto" density="comfortable"/></v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select label="Team" v-model="miscreport.team" :items="teamOptions"
                :rules="[req]" hide-details="auto" density="comfortable"/></v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select label="Offender" v-model="miscreport.offender" :items="offenderOptions"
                :rules="[req]" hide-details="auto" density="comfortable" @update:model-value="onOffenderChange"/></v-col>
            <v-col v-if="miscreport.offender === 'Player'" cols="12" sm="6" md="2">
              <v-select label="Player #" v-model="miscreport.playerNumber" :items="playerNumberOptions"
                :rules="[reqIfPlayer]" hide-details="auto" density="comfortable"/></v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field label="First Name" v-model="miscreport.playerNameFirst"
                hide-details="auto" density="comfortable" autocomplete="given-name"/></v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field label="Last Name" v-model="miscreport.playerNameLast"
                hide-details="auto" density="comfortable" autocomplete="family-name"/></v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field label="Player ID" v-model="miscreport.playerId"
                hide-details="auto" density="comfortable"/></v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select label="Incident Type" v-model="miscreport.incidentType" :items="incidentTypes"
                :rules="[req]" hide-details="auto" density="comfortable" @update:model-value="onIncidentTypeChange"/></v-col>
            <v-col cols="12" sm="6" md="5">
              <v-select 
                label="Misconduct" 
                v-model="miscreport.misconductItem" 
                v-model:menu="misconductMenuOpen" 
                ref="misconductSelectRef" 
                :items="misconductItems" 
                item-title="MisconductItem" 
                item-value="MisconductItem"
                :return-object="false" 
                :rules="[req]" 
                hide-details="auto" 
                density="comfortable" 
                class="left-align-select"
                @update:model-value="onMisconductItemChange"
              /></v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field label="Referee Action" v-model="miscreport.refereeAction"
                readonly hide-details="auto" density="comfortable"/></v-col>
            <v-col cols="12">
              <v-textarea label="Incident Description" v-model="miscreport.incidentDescription"
                :rules="[req]" rows="4" auto-grow hide-details="auto" density="comfortable" counter/></v-col>
          </v-row>

          <v-alert type="info" variant="tonal" border="start" class="mt-4">
            <div class="text-subtitle-2 mb-1">Field notes</div>
            <ul class="ml-4">
              <li>Offender: Player, Substitute/Substituted Player, Team Official, Spectator.</li>
              <li>Incident Type: Offense/Foul (contact) or Technical Law Infraction (non-contact such as dissent, FRD, delaying restart).</li>
            </ul>
          </v-alert>
        </v-container>
      </v-form>
    </div>

    <!-- Sticky actions -->
    <v-sheet class="cl-actions" elevation="2">
      <div class="flex gap-3 items-center">
        <v-btn color="success" prepend-icon="mdi-content-save" :loading="saving" @click="onSubmit">
          {{ isEdit ? 'Save Changes' : 'Create Misconduct' }}
        </v-btn>
        <v-btn color="error" variant="tonal" prepend-icon="mdi-close" @click="onClose">
          Cancel
        </v-btn>
        <v-spacer />
        <v-chip v-if="miscreport.offender === 'Player' && !miscreport.playerNumber" color="warning" label size="small">
          Player # required for Offender = Player
        </v-chip>
      </div>
    </v-sheet>
  </v-card>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue'
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc } from 'firebase/firestore'
import { db, listMisconductColRef, misconductColRef } from '@/firebase'
import { useRoleCaps } from '@/composables/useRoleCaps'

const props = defineProps({
  mreportId: { type: String, required: true },
  miscId: { type: String, default: null },
  teamHome: { type: String, default: '' },
  teamAway: { type: String, default: '' },
  league: { type: String, default: '' },
  userId: { type: String, required: true },
  reportUserId: { type: String, default: '' },  // ✅ NEW (referee who created the report)
})
const emit = defineEmits(['saved', 'closed'])

const isEdit = computed(() => !!props.miscId)

const formRef = ref(null)
const valid = ref(false)
const saving = ref(false)

const miscreport = reactive({
  league: props.league || '',
  group: '',
  time: '',
  offender: '',
  team: '',
  playerNumber: '',
  playerNameFirst: '',
  playerNameLast: '',
  playerId: '',
  incidentType: '',
  misconductCode: '',
  CIFCode: '',
  misconductItem: '',
  refereeAction: '',
  incidentDescription: '',
  mreportId: props.mreportId,
})

const incidentTypes = ref([])
const misconductItems = ref([])
const refereeActionsMap = ref(new Map())
const misconductItemsMap = ref(new Map())

const misconductSelectRef = ref(null)
const misconductMenuOpen = ref(false)

const offenderOptions = ['Player', 'Substitute/Substituted Player', 'Team Official', 'Spectator','Coach']
const teamOptions = [props.teamHome, props.teamAway].filter(Boolean)
const minuteOptions = Array.from({ length: 121 }, (_, i) => i)
const playerNumberOptions = ['00', '0', ...Array.from({ length: 100 }, (_, i) => String(i + 1))]

const caps = useRoleCaps()  // get user capabilities from composable

const req = (v) => (v === 0 || v === '0' || !!v) || 'Required'
const reqIfPlayer = (v) => (miscreport.offender !== 'Player' || (!!v && v !== '')) || 'Required when Offender = Player'

watch(() => props.miscId, async (id) => {
  // reset
  Object.assign(miscreport, {
    league: props.league || '',
    group: '',
    time: '',
    offender: '',
    team: '',
    playerNumber: '',
    playerNameFirst: '',
    playerNameLast: '',
    playerId: '',
    incidentType: '',
    misconductCode: '',
    CIFCode: '',
    misconductItem: '',
    refereeAction: '',
    incidentDescription: '',
    mreportId: props.mreportId,
  })

  if (!id) {
    // NEW: leave blank; defaults will be applied below
  } else {
    // EDIT: fetch and map from DB
    const snap = await getDoc(doc(db, 'misconduct', id))
    if (snap.exists()) Object.assign(miscreport, mapFromDb(snap.data()))
  }

  // hydrate picklists after DB load
  await fetchPicklistData()
  
  // Get and sort the misconduct items
  let items = misconductItemsMap.value.get(miscreport.incidentType) || []
  items.sort((a, b) => (a.SortOrder_MisconductItem ?? 0) - (b.SortOrder_MisconductItem ?? 0))
  misconductItems.value = items

  // EDIT: keep DB value if it exists in list; don't auto-pick a default
  if (miscreport.misconductItem) {
    const found = misconductItems.value.find(i => i.MisconductItem === miscreport.misconductItem)
    if (found) onMisconductItemChange(found.MisconductItem) // sync codes/actions
    } else if (!props.miscId && misconductItems.value.length) {
      // CREATE: choose first only when creating
      miscreport.misconductItem = misconductItems.value[0].MisconductItem
      onMisconductItemChange(miscreport.misconductItem)
    }
  incidentTypes.value = Array.from(misconductItemsMap.value.keys());
}, { immediate: true })

onMounted(async () => {})

function mapFromDb(x) {
  return {
    league: x.League || props.league || '',
    group: '',
    time: x.Time ?? '',
    offender: x.Offender ?? '',
    team: x.Team ?? '',
    playerNumber: x.PlayerNumber ?? '',
    playerNameFirst: x.PlayerNameFirst ?? '',
    playerNameLast: x.PlayerNameLast ?? '',
    playerId: x.PlayerId ?? '',
    incidentType: x.IncidentType ?? '',
    refereeAction: x.RefereeAction ?? '',
    incidentDescription: x.IncidentDescription ?? '',
    misconductCode: x.MisconductCode ?? '',
    CIFCode: x.CIFCode ?? '',
    misconductItem: x.MisconductItem ?? null,
    mreportId: x.MatchReportID || props.mreportId,
  }
}

function setDefaultsFromItems() {
  const list = misconductItems.value || [];
  // never force defaults during edit
  if (isEdit.value) 
    return
  if (!miscreport.misconductItem && list.length) {
    miscreport.misconductItem = list[0].MisconductItem
    onMisconductItemChange(miscreport.misconductItem)
  }
}

async function fetchPicklistData() {
  try {
    // Resolve RuleFramework (group)
    let group = 'IFAB'
    const leagueVal = miscreport.league
    if (leagueVal) {
      const leaguesRef = collection(db, 'leagues')
      const q = query(leaguesRef, where('League', '==', leagueVal))
      const leagueSnapshot = await getDocs(q)
      if (!leagueSnapshot.empty) group = leagueSnapshot.docs[0].data().RuleFramework || 'IFAB'
    }
    miscreport.group = group

/*    const snapshot = await getDocs(listMisconductColRef) */
    const q = query(listMisconductColRef, 
      where('Group', '==', group),  /* filter misconduct list by league */
      where('Offender', '==', miscreport.offender))  /* filter misconduct list by selected Offender */
    const snapshot = await getDocs(q)
    const offenderData = { incidentTypes: new Set(), misconductItems: new Map(), refereeActions: new Map() }

    snapshot.forEach(d => {
      const x = d.data()
      if (x.Group === group && x.Offender === miscreport.offender) {
        offenderData.incidentTypes.add(x.IncidentType)
        if (!offenderData.misconductItems.has(x.IncidentType)) offenderData.misconductItems.set(x.IncidentType, [])
        offenderData.misconductItems.get(x.IncidentType).push({
          MisconductItem: x.MisconductItem,
          MisconductCode: x.MisconductCode,
          CIFCode: x.CIFCode,
          SortOrder_MisconductItem: x.SortOrder_MisconductItem,
          RefereeAction: x.RefAction,
        })
        if (!offenderData.refereeActions.has(x.MisconductItem)) offenderData.refereeActions.set(x.MisconductItem, [])
        offenderData.refereeActions.get(x.MisconductItem).push(x.RefAction)
      }
    })

    // Sort misconduct items within each incident type by SortOrder_MisconductItem
    offenderData.misconductItems.forEach(items => {
      items.sort((a, b) => (a.SortOrder_MisconductItem ?? 0) - (b.SortOrder_MisconductItem ?? 0))
    })

    incidentTypes.value = Array.from(offenderData.incidentTypes)
    misconductItemsMap.value = offenderData.misconductItems
    refereeActionsMap.value = offenderData.refereeActions
  } catch (e) { console.error('fetchPicklistData', e) }
}

async function onOffenderChange() {
  miscreport.incidentType = ''
  miscreport.misconductItem = ''
  miscreport.refereeAction = ''
  await fetchPicklistData()
  if (!isEdit.value) setDefaultsFromItems()
}

async function onIncidentTypeChange() {
  miscreport.misconductItem = ''
  miscreport.refereeAction = ''

  await fetchPicklistData();

  // Get and sort the misconduct items by SortOrder_MisconductItem
  let items = misconductItemsMap.value.get(miscreport.incidentType) || []
  items.sort((a, b) => (a.SortOrder_MisconductItem ?? 0) - (b.SortOrder_MisconductItem ?? 0))
  misconductItems.value = items

  // Open the Misconduct dropdown
  nextTick(() => {
    misconductMenuOpen.value = true
  })
  // CREATE-only default; on edit, let the existing value stand if user didn't change it
  if (!isEdit.value && misconductItems.value.length) {
    const firstItem = misconductItems.value[0]
    if (!miscreport.misconductItem) {
      miscreport.misconductItem = firstItem.MisconductItem
    }
  onMisconductItemChange(firstItem.MisconductItem)  // always sync RefereeAction
  }
}

function onMisconductItemChange(val) {
  // val is a string (MisconductItem)
  const it = (misconductItems.value || []).find(x => x.MisconductItem === val);
  if (!it) {
    miscreport.misconductCode = '';
    miscreport.CIFCode = '';
    miscreport.refereeAction = '';
    return;
  }
  miscreport.misconductCode = it.MisconductCode || '';
  miscreport.CIFCode       = it.CIFCode || '';
  const actions = refereeActionsMap.value.get(it.MisconductItem) || [];
  miscreport.refereeAction = actions[0] || '';
}

async function onSubmit() {
  const res = await formRef.value?.validate()
  if (!res?.valid) return
  saving.value = true
  try {
    if (isEdit.value) {
      const miscdocRef = doc(db, 'misconduct', props.miscId)
      await updateDoc(miscdocRef, toDbPayload())
      emit('saved', { id: props.miscId, mode: 'edit' })
    } else {
      const payload = {
        ...toDbPayload(),
        MatchReportID: props.mreportId,
        MatchReportUserID: props.reportUserId || props.userId,  // ✅ use the report creator if available
        League: miscreport.league
        }
      const refDoc = await addDoc(misconductColRef, payload)
      emit('saved', { id: refDoc.id, mode: 'add' })
    }
  } catch (e) {
    console.error('save misconduct', e)
  } finally {
    saving.value = false
  }
}

function toDbPayload() {
  return {
    Time: miscreport.time,
    Offender: miscreport.offender,
    Team: miscreport.team,
    PlayerNumber: miscreport.playerNumber,
    PlayerNameFirst: miscreport.playerNameFirst,
    PlayerNameLast: miscreport.playerNameLast,
    PlayerId: miscreport.playerId,
    IncidentType: miscreport.incidentType,
    IncidentDescription: miscreport.incidentDescription,
    MisconductCode: miscreport.misconductItem?.MisconductCode || miscreport.misconductCode || '',
    CIFCode: miscreport.misconductItem?.CIFCode || miscreport.CIFCode || '',
    MisconductItem: miscreport.misconductItem || '',
    RefereeAction: miscreport.refereeAction,
  }
}

function onClose() { emit('closed') }
</script>

<style scoped>
.cl-content { max-height: 70vh; overflow: auto; padding: 16px; }
.cl-actions { position: sticky; bottom: 0; padding: 12px 16px; background: var(--v-theme-surface); border-top: 1px solid var(--v-theme-outline-variant); }
.flex { display: flex; }
.items-center { align-items: center; }
.gap-3 { gap: 12px; }
</style>

<style>
/* Global styles for dropdown alignment - not scoped */
.v-list-item-title {
  text-align: left !important;
}

.v-list-item {
  text-align: left !important;
}

.v-list-item__content {
  text-align: left !important;
  align-items: flex-start !important;
}

.v-select .v-field__input {
  text-align: left !important;
}

.v-menu .v-list {
  text-align: left !important;
}

.v-menu .v-list-item {
  justify-content: flex-start !important;
}
</style>